/**
 * Yup validation schemas for authentication forms.
 *
 * Provides client-side validation and type safety for auth-related forms.
 */

import * as Yup from 'yup'
import { VALIDATION_RULES, PASSWORD_REQUIREMENTS } from './constants'

/** Email validation schema. Used for button disable logic only. */
export const emailValidationSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required'),
})

/** Password validation schema with minimum length requirement. */
export const passwordValidationSchema = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .min(
      VALIDATION_RULES.PASSWORD_MIN_LENGTH,
      `Password must be at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`,
    ),
})

/** Combined email + password validation schema for login. */
export const loginPasswordSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(
      VALIDATION_RULES.PASSWORD_MIN_LENGTH,
      `Password must be at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`,
    ),
})

/** OTP code validation schema for 6-digit verification codes. */
export const otpCodeValidationSchema = Yup.object({
  code: Yup.string()
    .trim()
    .required('Verification code is required')
    .matches(/^\d+$/, 'Code must contain only numbers')
    .length(
      VALIDATION_RULES.OTP_CODE_LENGTH,
      `Code must be exactly ${VALIDATION_RULES.OTP_CODE_LENGTH} digits`,
    ),
})

/**
 * Comprehensive password validation schema for registration/reset flows.
 * Validates all password requirements: length, uppercase, lowercase, number,
 * symbol.
 */
export const setPasswordValidationSchema = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .min(
      VALIDATION_RULES.PASSWORD_MIN_LENGTH,
      `Password must be at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`,
    )
    .matches(
      PASSWORD_REQUIREMENTS.PATTERNS.LOWERCASE,
      'Password must contain at least one lowercase character',
    )
    .matches(
      PASSWORD_REQUIREMENTS.PATTERNS.UPPERCASE,
      'Password must contain at least one uppercase character',
    )
    .matches(
      PASSWORD_REQUIREMENTS.PATTERNS.NUMBER,
      'Password must contain at least one number',
    )
    .matches(
      PASSWORD_REQUIREMENTS.PATTERNS.SYMBOL,
      `Password must contain at least one special character (${PASSWORD_REQUIREMENTS.ALLOWED_SYMBOLS})`,
    ),
})

/** TypeScript types derived from validation schemas */
export type EmailFormValues = Yup.InferType<typeof emailValidationSchema>
export type PasswordFormValues = Yup.InferType<typeof passwordValidationSchema>
export type LoginPasswordFormValues = Yup.InferType<typeof loginPasswordSchema>
export type OTPCodeFormValues = Yup.InferType<typeof otpCodeValidationSchema>
export type SetPasswordFormValues = Yup.InferType<
  typeof setPasswordValidationSchema
>
